import { Analytics } from "@omni/types/api/product/bff/shared/analytics.model";
import { Feature } from "@omni/types/api/product/bff/shared/feature.model";
import { attempt } from "@omni/utils/function.utils";
import { useMemo } from "react";

export const YIELD_PER_PACKAGE_FEATURE_CODE =
    "Hubo_Classification_Catalog/GPC/10003874.4.362";

export const YIELD_CONTENT_FEATURE_CODE =
    "Hubo_Classification_Catalog/GPC/GPC.3.009";

export const YIELD_PER_LITER_FEATURE_CODE =
    "Hubo_Classification_Catalog/GPC/10003874.4.385";

import { YieldCalculatorResult } from "./yieldCalculator.types";

export const LayerMap: Record<string, number> = {
    "primers": 1,
    "default": 2
};

const getValue = (feature: Feature): number => {
    return attempt(() => {
        const formattedValue = feature.featureValues.at(0)?.formattedValue;
        if (!formattedValue) {
            return 0;
        }
        return parseFloat(formattedValue.replace(",", "."));
    }, 0);
};
const getUnit = (feature: Feature): string => {
    return feature.unit?.name || "";
};

export const useYieldFeatureValues = ({
    features,
    analytics
}: {
    features: Array<Feature>;
    analytics?: Analytics;
}) => {
    const yieldPackageFeature = useMemo(() => {
        if (!features) return undefined;

        return features.find((f) => f.code === YIELD_PER_PACKAGE_FEATURE_CODE);
    }, [features]);

    const yieldContentFeature = useMemo(() => {
        if (!features) return undefined;

        return features.find((f) => f.code === YIELD_CONTENT_FEATURE_CODE);
    }, [features]);

    const yieldPerLiterFeature = useMemo(() => {
        if (!features) return undefined;

        return features.find((f) => f.code === YIELD_PER_LITER_FEATURE_CODE);
    }, [features]);

    const { yieldUnit, yieldPerPackage } = useMemo(() => {
        if (yieldContentFeature && yieldPerLiterFeature) {
            return {
                yieldUnit: getUnit(yieldPerLiterFeature),
                yieldPerPackage:
                    getValue(yieldContentFeature) *
                    getValue(yieldPerLiterFeature)
            };
        }
        return yieldPackageFeature
            ? {
                  yieldUnit: getUnit(yieldPackageFeature),
                  yieldPerPackage: getValue(yieldPackageFeature)
              }
            : {
                  yieldUnit: undefined,
                  yieldPerPackage: undefined
              };
    }, [yieldPackageFeature, yieldContentFeature, yieldPerLiterFeature]);

    const yieldLayers = useMemo(() => {
        const identifier = analytics?.superCategoryCode || "default";
        return LayerMap[identifier] || LayerMap["default"];
    }, [analytics]);

    const canShowCalculator = useMemo(() => {
        return yieldUnit && yieldPerPackage;
    }, [yieldPerPackage, yieldUnit]);

    const calculateQuantity = (surface: number): YieldCalculatorResult => {
        if (!yieldPerPackage) {
            return {
                quantity: 0
            };
        }
        return {
            quantity: Math.ceil((surface / yieldPerPackage) * yieldLayers)
        };
    };

    return {
        canShowCalculator,
        calculateQuantity,
        yieldUnit,
        yieldLayers
    };
};
